import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface AgentTool {
  id: number;
  name: string;
}

export interface AgentDocument {
  id: number;
  filename: string;
  content_type: string;
  byte_size: number;
  ocr_status: string;
  thumbnail_url: string | null;
}

export interface AgentSchema {
  id: number;
  version: number;
  prompt: string;
  schema: Record<string, unknown>;
  example: Record<string, unknown>;
  status: "pending" | "processing" | "completed" | "error";
  error: string | null;
  render_spec: Record<string, unknown> | null;
  active: boolean;
  created_at: string;
}

export interface Agent {
  id: number;
  label: string;
  user_facing_question: string | null;
  system_prompt: string | null;
  system_prompt_type: string;
  profile_class: string | null;
  slug: string | null;
  published_at: string | null;
  icon: string | null;
  position: number;
  qr_code: boolean;
  active_schema: AgentSchema | null;
  marketing_title: string | null;
  marketing_content: string | null;
  illustration_url: string | null;
  tool_ids: number[];
  tools: AgentTool[];
  document_ids: number[];
  documents: AgentDocument[];
  created_at: string;
  updated_at: string;
}

export interface AgentInput {
  label?: string;
  user_facing_question?: string;
  system_prompt?: string;
  system_prompt_type?: string;
  profile_class?: string | null;
  published_at?: string | null;
  icon?: string | null;
  position?: number;
  qr_code?: boolean;
  marketing_title?: string | null;
  marketing_content?: string | null;
  tool_ids?: number[];
  document_ids?: number[];
}

export interface PreviewTarget {
  id: number;
  name: string;
}

const BASE_URL = "/system/agentic/agents";

// API functions
export const agentApi = {
  list: async (): Promise<Agent[]> => {
    const { data } = await axios.get(`${BASE_URL}.json`);
    return data;
  },

  get: async (id: number): Promise<Agent> => {
    const { data } = await axios.get(`${BASE_URL}/${id}.json`);
    return data;
  },

  create: async (input: AgentInput): Promise<Agent> => {
    const { data } = await axios.post(`${BASE_URL}.json`, { agent: input });
    return data;
  },

  update: async (id: number, input: AgentInput & { illustration?: File | null }): Promise<Agent> => {
    if (input.illustration) {
      const formData = new FormData();
      formData.append("agent[illustration]", input.illustration);
      const { illustration, ...rest } = input;
      Object.entries(rest).forEach(([key, value]) => {
        if (value === undefined) return;
        if (key === "tool_ids" || key === "document_ids") {
          (value as number[]).forEach((v) => formData.append(`agent[${key}][]`, String(v)));
        } else {
          formData.append(`agent[${key}]`, value === null ? "" : String(value));
        }
      });
      const { data } = await axios.patch(`${BASE_URL}/${id}.json`, formData);
      return data;
    }
    const { data } = await axios.patch(`${BASE_URL}/${id}.json`, {
      agent: input,
    });
    return data;
  },

  destroy: async (id: number): Promise<void> => {
    await axios.delete(`${BASE_URL}/${id}.json`);
  },

  reorder: async (agents: Array<{ id: number; position: number }>): Promise<void> => {
    await axios.post(`${BASE_URL}/reorder.json`, { agents });
  },

  preview: async (endpoint: string, q?: string): Promise<PreviewTarget[]> =>
    (await axios.get(`${BASE_URL}/${endpoint}.json`, { params: q ? { q } : {} })).data,
};

// Query keys
export const agentKeys = {
  all: ["agents"] as const,
  detail: (id: number) => ["agents", id] as const,
  preview: (profileClass: string) => ["agents", "preview", profileClass] as const,
};

// Hooks
export function useAgents() {
  return useQuery({
    queryKey: agentKeys.all,
    queryFn: agentApi.list,
  });
}

export function useAgent(id: number) {
  return useQuery({
    queryKey: agentKeys.detail(id),
    queryFn: () => agentApi.get(id),
    enabled: !!id,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: agentApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: AgentInput }) =>
      agentApi.update(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(id) });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: agentApi.destroy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}

const PREVIEW_ENDPOINTS: Record<string, string> = {
  "Profile::Candidat": "preview_candidats",
  "Profile::Enseigne": "preview_enseignes",
  "Profile::Immo": "preview_immos",
  "Profile::Partner": "preview_partners",
  "Profile::ShoppingCenter": "preview_shopping_centers",
  "Profile::Union": "preview_unions",
  "Profile::Ville": "preview_villes",
  "Location": "preview_locations",
  "PointOfSale": "preview_point_of_sales",
  "User": "preview_users",
};

export function usePreviewTargets(profileClass: string, q?: string) {
  const endpoint = PREVIEW_ENDPOINTS[profileClass];
  return useQuery({
    queryKey: [...agentKeys.preview(profileClass), q || ""],
    queryFn: () => agentApi.preview(endpoint, q),
    enabled: !!endpoint,
  });
}

export function useReorderAgents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: agentApi.reorder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
    },
  });
}
